<template>
  <div
    class="product__thumbnail-slider"
    ref="thumbnailSlider"
    @mousedown="mouseDownHandler"
  >
    <template v-if="items.length">
      <div
        class="product__thumbnail"
        :class="{
          'product__thumbnail--active': index === selectedSlideIndex,
        }"
        v-for="(item, index) of items"
        :key="index"
        @click="thumbnailClick(index)"
      >
        <sib-image loading="lazy" v-bind="item.image"></sib-image>
        <span
          class="play-video-button"
          v-if="item.mediaContentType === PRODUCT_MEDIA_VIDEO"
        >
        </span>
      </div>
      <div
        class="product__thumbnail product__thumbnail-preview"
        :class="{
          [`product__thumbnail--active`]: items.length === selectedSlideIndex,
        }"
        v-if="productStore.hasPreview"
        @click="thumbnailClick(items.length)"
      >
        <span class="preview-note"> Preview </span>
        <img
          :src="
            productStore.productPreviewImage ||
            getSizedImageUrl(items[0].image.src, buildSizeString(200))
          "
          alt="preview image"
        />
      </div>
    </template>
    <template v-else>
      <sib-skeleton-loading
        v-for="index in 6"
        :key="index"
        size="square"
      ></sib-skeleton-loading>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, Ref, computed } from '@vue/reactivity'
import { onMounted, watch } from 'vue'
import { Image as ImageType } from '@/types'
import { PRODUCT_MEDIA_VIDEO, cloneDeep } from '@/utils'
import { getSizedImageUrl, buildSizeString } from '@/utils/image'
import useProductStore from '@/store/product'

interface ProductImage {
  image: ImageType
  mediaContentType: string
}
const props = withDefaults(
  defineProps<{
    images: ProductImage[]
    selectedSlideIndex: number
    scrollToLeft?: boolean
  }>(),
  {
    images: () => [],
    selectedSlideIndex: 0,
    scrollToLeft: false,
  }
)

const productStore = useProductStore()

// resize product images
const items = computed(() => {
  if (!props.images?.length) return []
  return props.images.map((image) => {
    const clonedImage = cloneDeep(image)
    clonedImage.image.sources = [
      {
        size: 100,
      },
    ]
    return clonedImage
  })
})

const thumbnailSlider: Ref<HTMLElement | null> = ref(null)

const isDragging = ref(false)
const pos = reactive({
  top: 0,
  left: 0,
  x: 0,
  y: 0,
})

const emit = defineEmits<{
  (e: 'change', index: number): void
}>()

const thumbnailClick = (index: number) => {
  if (isDragging.value) return
  emit('change', index)
}

// make the thumbnail slider draggable
const mouseDownHandler = function (e: MouseEvent) {
  if (thumbnailSlider.value) {
    pos.top = thumbnailSlider.value.scrollTop
    thumbnailSlider.value.style.scrollBehavior = 'auto'
  }
  pos.x = e.clientX
  pos.y = e.clientY

  document.addEventListener('mousemove', mouseMoveHandler)
  document.addEventListener('mouseup', mouseUpHandler)
}

const mouseMoveHandler = function (e: MouseEvent) {
  // how far the mouse has been moved
  const dy = e.clientY - pos.y
  const dx = e.clientX - pos.x
  if (Math.abs(dy) > 2 || Math.abs(dx) > 2) {
    isDragging.value = true
    // scroll the element
    if (thumbnailSlider.value) {
      thumbnailSlider.value.scrollTop = pos.top - dy
    }
  }
}

const mouseUpHandler = function () {
  document.removeEventListener('mousemove', mouseMoveHandler)
  document.removeEventListener('mouseup', mouseUpHandler)
  if (thumbnailSlider.value) {
    thumbnailSlider.value.style.scrollBehavior = 'smooth'
  }
  // Wait a tick to run it after click event
  setTimeout(() => {
    isDragging.value = false
  })
}

// scroll to active slide
const scrollToActive = (index: number) => {
  if (thumbnailSlider.value) {
    const childs = thumbnailSlider.value.children
    if (childs.length > 0) {
      const child = childs[index] as HTMLElement
      if (child) {
        // scroll the selected element to the center of the thumbnail slider
        if (props.scrollToLeft) {
          thumbnailSlider.value.scrollLeft =
            child.offsetLeft -
            (thumbnailSlider.value.offsetWidth - child.offsetWidth) / 2
        } else {
          thumbnailSlider.value.scrollTop =
            child.offsetTop -
            (thumbnailSlider.value.offsetHeight - child.offsetHeight) / 2
        }
      }
    }
  }
}

watch(
  () => props.selectedSlideIndex,
  (index: number) => {
    scrollToActive(index)
  }
)

onMounted(() => {
  scrollToActive(props.selectedSlideIndex)
})
</script>

<style lang="scss">
.product__thumbnail-slider {
  width: 100px;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  display: none;
  flex-direction: column;
  gap: 10px;
  overflow: auto;
  cursor: grab;
  scroll-behavior: smooth;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @include media-up(501px) {
    display: flex;
  }

  @include media-md-up {
    display: none;
  }

  @include media-lg-up {
    display: flex;
  }
}

.product__thumbnail {
  position: relative;
  user-select: none;
  width: 100px;
  min-height: 100px;
  background-color: var(--background-color);

  img {
    -webkit-user-drag: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &::before {
    content: '';
    display: block;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.5);
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
  }

  &--active {
    border: solid 2px var(--color-primary);

    &::before {
      background-color: rgba(255, 255, 255, 0);
    }
  }
}

.product__thumbnail-preview {
  .preview-note {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 1.25rem;
    color: var(--color-primary);
    font-weight: 600;
    z-index: 1;
    @include media-md-down {
      font-size: 12px;
    }
  }
}

.play-video-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: var(--base-z-index);
  max-width: 6rem;
  width: 40%;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  background: rgba(243, 243, 243, 0.5);

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-35%, -50%);
    border-style: solid;
    border-color: transparent transparent transparent #fff;
    border-width: 0.75rem 0 0.75rem 1.25rem;
  }
}
</style>
